import type { Reference } from '@medplum/fhirtypes'
import { codeableConcept } from '@seen/core/codeableConcept'
import type { CodingAnswerOption } from '@seen/core/codings'
import { referenceDisplay } from '@seen/core/display'
import { invariant } from '@seen/core/invariant'
import type { ExtendedObservation } from '@seen/core/models/observation'
import { resolveId } from '@seen/core/references'
import { Button, cn } from '@seen/ui'
import type { JSX, ReactNode, Ref } from 'react'
import { ReferenceNameplate } from '#src/components/reference-picker/ReferenceNameplate.tsx'
import { ObservationAnswerPicker } from '#src/components/rich-text-editor/ObservationNode/ObservationAnswerPicker.tsx'
import { answerValueClass } from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { ObservationNodeFieldForm } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFieldForm.tsx'
import {
  ObservationNodeFrame,
  type ObservationNodeFrameProps,
} from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import { ObservationNoteFieldForm } from '#src/components/rich-text-editor/ObservationNode/ObservationNoteFieldForm.tsx'
import { ObservationNoteSlot } from '#src/components/rich-text-editor/ObservationNode/ObservationNoteSlot.tsx'
import { ObservationReferencePicker } from '#src/components/rich-text-editor/ObservationNode/ObservationReferencePicker.tsx'
import { ObservationRichTextEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationRichTextEditor.tsx'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'

interface BoundEditorProps {
  observation: ExtendedObservation
  attributes: ObservationNodeAttributes
  // Shown after the fields, inside the same box.
  trailing?: ReactNode
  wrapperRef?: Ref<HTMLSpanElement>
  className?: string
  details?: ObservationNodeFrameProps['details']
  onDetailsOpenChange?: (open: boolean) => void
  updateAttributes?: ObservationNodeFrameProps['updateAttributes']
  // In the details dialog: the note editor always shows and takes several
  // lines, where in a line it is one line and shows only once used.
  inDetails?: boolean
  autofocusNote?: boolean
}

// Changes the answer and note of a recorded coded observation in place.
export function ObservationChoiceEditor({
  observation,
  answerOptions,
  answerValueSet,
  scaleType,
  sideBySide = false,
  initialOpen = false,
  attributes,
  trailing,
  wrapperRef,
  className,
  details,
  onDetailsOpenChange,
  updateAttributes,
  inDetails = false,
  autofocusNote = false,
}: BoundEditorProps & {
  answerOptions: CodingAnswerOption[]
  answerValueSet: string | null
  scaleType: 'Ord' | 'Nom'
  sideBySide?: boolean
  // Open the answers straight away, as happens when a recorded answer is
  // clicked to be changed.
  initialOpen?: boolean
}): JSX.Element {
  const practitioner = useCurrentPractitioner()

  return (
    <ObservationNodeFrame
      attributes={attributes}
      label={observation.display}
      wrapperRef={wrapperRef}
      className={cn('min-w-0', className)}
      noHover
      trailing={trailing}
      details={details}
      onDetailsOpenChange={onDetailsOpenChange}
      updateAttributes={updateAttributes}
      participant={observation.subject}
    >
      <ObservationNodeFieldForm
        instance={observation}
        valueFn={currentAnswer}
        patchFn={(o, answer) => {
          invariant(answer, 'answer is required')
          o.valueCodeableConcept = codeableConcept(answer)
          o.performerPractitioner = practitioner.reference
        }}
        className="min-w-0"
        render={({ field: { value, onChange, disabled, ...rest } }) => (
          <ObservationAnswerPicker
            answerOptions={answerOptions}
            answerValueSet={answerValueSet}
            scaleType={scaleType}
            codingDisplay={observation.display}
            onSelect={onChange}
            sideBySide={sideBySide}
            value={value}
            disabled={disabled}
            initialOpen={initialOpen}
          >
            <Button
              variant="unstyled"
              size="unstyled"
              className={answerValueClass()}
              disabled={disabled}
              {...rest}
            >
              {answerOptions.find((option) => option.code === value?.code)
                ?.display ?? value?.display}
            </Button>
          </ObservationAnswerPicker>
        )}
      />
      <ObservationNoteSlot
        note={observation.noteFirstText}
        inDetails={inDetails}
      >
        &nbsp;&nbsp;
        <div
          className="mr-[0.1875rem]" // Accounts for the shift below.
        />
        <ObservationNoteFieldForm
          observation={observation}
          singleLine={!inDetails}
          autofocus={autofocusNote}
          className={cn(
            'grow', // So that the editor can grow to fill the available horizontal space when the content goes onto multiple lines.
            '-ml-[0.1875rem]' // For visual alignment when the content goes onto multiple lines.
          )}
        />
      </ObservationNoteSlot>
    </ObservationNodeFrame>
  )
}

// Changes the record and note of an observation answered with a record.
export function ObservationReferenceEditor({
  observation,
  resourceType,
  initialOpen = false,
  attributes,
  trailing,
  wrapperRef,
  className,
  details,
  onDetailsOpenChange,
  updateAttributes,
  inDetails = false,
  autofocusNote = false,
}: BoundEditorProps & {
  resourceType: string
  initialOpen?: boolean
}): JSX.Element {
  const practitioner = useCurrentPractitioner()
  const subject = observation.subject

  return (
    <ObservationNodeFrame
      attributes={attributes}
      label={observation.display}
      wrapperRef={wrapperRef}
      className={cn('min-w-0', className)}
      noHover
      trailing={trailing}
      details={details}
      onDetailsOpenChange={onDetailsOpenChange}
      updateAttributes={updateAttributes}
      participant={observation.subject}
    >
      <ObservationNodeFieldForm
        instance={observation}
        valueFn={currentFocus}
        patchFn={(o, focus) => {
          invariant(focus, 'focus is required')
          o.focusReference = focus
          o.performerPractitioner = practitioner.reference
        }}
        className="min-w-0"
        render={({ field: { value, onChange, disabled, ...rest } }) => (
          <ObservationReferencePicker
            resourceType={resourceType}
            patientId={subject ? resolveId(subject) : undefined}
            value={value?.reference}
            onSelect={onChange}
            initialOpen={initialOpen}
          >
            <Button
              variant="unstyled"
              size="unstyled"
              aria-label="Change"
              className={answerValueClass()}
              disabled={disabled}
              {...rest}
            >
              {value?.reference ? (
                <ReferenceNameplate
                  resourceType={resourceType}
                  reference={value.reference}
                />
              ) : (
                referenceDisplay(value ?? undefined)
              )}
            </Button>
          </ObservationReferencePicker>
        )}
      />
      <ObservationNoteSlot
        note={observation.noteFirstText}
        inDetails={inDetails}
      >
        &nbsp;&nbsp;
        <div
          className="mr-[0.1875rem]" // Accounts for the shift below.
        />
        <ObservationNoteFieldForm
          observation={observation}
          singleLine={!inDetails}
          autofocus={autofocusNote}
          className={cn(
            'grow', // So that the editor can grow to fill the available horizontal space when the content goes onto multiple lines.
            '-ml-[0.1875rem]' // For visual alignment when the content goes onto multiple lines.
          )}
        />
      </ObservationNoteSlot>
    </ObservationNodeFrame>
  )
}

// Changes the text of a recorded narrative observation in place.
export function ObservationTextEditor({
  observation,
  autofocus = false,
  attributes,
  trailing,
  wrapperRef,
  className,
  details,
  onDetailsOpenChange,
  updateAttributes,
}: BoundEditorProps & { autofocus?: boolean }): JSX.Element {
  const practitioner = useCurrentPractitioner()

  return (
    <ObservationNodeFrame
      layout="block"
      attributes={attributes}
      label={observation.display}
      wrapperRef={wrapperRef}
      className={cn('min-w-0', className)}
      noHover
      trailing={trailing}
      details={details}
      onDetailsOpenChange={onDetailsOpenChange}
      updateAttributes={updateAttributes}
      participant={observation.subject}
    >
      <div
        className="mr-[0.1875rem]" // Accounts for the shift below.
      />
      <ObservationNodeFieldForm
        instance={observation}
        valueFn={(o) => o.valueString}
        patchFn={(o, v) => {
          o.valueString = v?.trim() ?? undefined
          o.performerPractitioner = practitioner.reference
        }}
        debounceTyping
        render={({ field }) => (
          <ObservationRichTextEditor
            placeholder="Add text…"
            autofocus={autofocus ? 'end' : false}
            {...field}
          />
        )}
        className={cn(
          // In the details it fills the dialog; in the note it is sized to
          // its text, wrapping only once the text needs the line's width.
          details === 'none' ? 'grow' : 'min-w-[8rem] max-w-full',
          '-ml-[0.1875rem]' // For visual alignment when the content goes onto multiple lines.
        )}
      />
    </ObservationNodeFrame>
  )
}

function currentFocus(observation: ExtendedObservation): Reference | undefined {
  return observation.focusReference
}

// The recorded answer with its label falling back to the Observation's own
// text, so an answer missing from the inline list still shows.
function currentAnswer(
  observation: ExtendedObservation
): CodingAnswerOption | undefined {
  const concept = observation.valueCodeableConcept
  const coding = concept?.coding?.[0]
  if (!coding?.code) {
    return undefined
  }
  const option: CodingAnswerOption = {
    code: coding.code,
    display: concept?.text ?? coding.display ?? coding.code,
  }
  if (coding.system) {
    option.system = coding.system
  }
  return option
}
